<template>
  <div class="w-full h-full pt-5">
    <div class="flex">
      <back-button
        label="Back"
        @onClick="$router.back()"
        variant="secondary"
        class="mr-4"
      />
      <div
        class="mt-4"
        style="border-left: 1px solid #878e99; height: 1.5rem"
      />
      <h1 class="text-xl text-left font-extrabold ml-4 mt-4 w-auto">
        Employee Agreement
      </h1>
      <div class="w-7/12 mt-5 ml-8">
        <Breadcrumb :items="breadcrumbs" />
      </div>
    </div>

    <template v-if="isLoading">
      <div class="mt-20 h-full">
        <Loader size="xxs" :loader-image="false" />
      </div>
    </template>

    <template v-else>
      <template>
        <div class="p-5 flex flex-col shadow m-5 rounded-md gap-4">
          <template>
            <div class="flex justify-between items-center">
              <span
                class="font-bold text-lg leading-5 tracking-normal capitalize text-darkPurple"
              >
                Agreement Data for Employee
              </span>
            </div>
          </template>
          <div class="grid grid-cols-12 gap-7">
            <div class="col-span-4 mt-1" v-if="payload.status === 'draft'">
              <div class="text-sm text-jet -mb-1">Financial Year</div>
              <div
                class="date-flex mt-2"
                :style="periodError ? 'border : 1px solid red' : ''"
              >
                <datepicker
                  placeholder="--Select Day & Month & Year--"
                  style="width: 100%; outline: none"
                  input-class="date-input"
                  format="yyyy"
                  minimum-view="year"
                  v-model="$route.params.year"
                  @selected="periodSelect"
                />
              </div>
            </div>
            <div class="col-span-4 mt-1" v-else>
              <c-text
                placeholder="--Enter--"
                variant="w-full"
                v-model="$route.params.year"
                class="mt-1"
                :disabled="true"
                label="Financial Year"
              />
            </div>

            <div class="col-span-4">
              <c-text
                placeholder="--Enter--"
                variant="w-full"
                v-model="templateName"
                class="mt-1"
                :disabled="true"
                label="Template"
              />
            </div>

            <div class="col-span-4">
              <c-text
                placeholder="--Enter--"
                variant="w-full"
                class="mt-1"
                :disabled="true"
                v-model="agreementData.description"
                label="Description"
              />
            </div>
          </div>
          <template>
            <div
              class="border border-dashed bg-white border-romanSilver rounded-md gap-4 p-3 mt-4"
            >
              <div class="flex items-center w-full gap-2 cursor-pointer">
                <span
                  class="font-black text-xs leading-5 uppercase tracking-wider text-romanSilver whitespace-nowrap"
                >
                  Performance Details
                </span>

                <div
                  class="flex-grow border-t border-solid border-romanSilver h-0"
                ></div>

                <Icon
                  :icon-name="'chevronDown'"
                  class-name="text-darkPurple self-center"
                  size="xms"
                />
              </div>

              <div
                class="grid gap-2 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 mt-3"
              >
                <div class="flex w-full p-3 shadow rounded-md gap-2">
                  <Icon
                    size="s"
                    icon-name="doc-cycle"
                    class-name="text-romanSilver self-start pt-1"
                  />
                  <div class="flex flex-col">
                    <span
                      class="font-black text-xs leading-5 tracking-wide text-darkPurple uppercase"
                    >
                      {{ agreementData.cycle }}
                    </span>
                    <span
                      class="font-bold text-sm leading-6 tracking-wide text-jet uppercase"
                    >
                      {{ agreementData.description }}
                    </span>
                  </div>
                </div>

                <div class="flex w-full p-3 shadow rounded-md gap-2">
                  <Icon
                    size="xs"
                    icon-name="calendar"
                    class-name="text-romanSilver self-start pt-1"
                  />
                  <div class="flex flex-col">
                    <span
                      class="font-black text-xs leading-5 tracking-wide text-carrotOrange uppercase"
                    >
                      Period
                    </span>
                    <span
                      class="font-bold text-sm leading-6 tracking-wide text-jet uppercase"
                    >
                      {{
                        $DATEFORMAT(
                          new Date(agreementData.period.start),
                          "MMMM dd, yyyy"
                        )
                      }}
                      -
                      {{
                        $DATEFORMAT(
                          new Date(agreementData.period.end),
                          "MMMM dd, yyyy"
                        )
                      }}
                    </span>
                  </div>
                </div>
                <!-- <PerformanceDetails :agreementCycles="agreementCycles"/> -->
                 
                <div v-for="(cycle, index) in agreementCycles" :key="index"
                  class="flex w-full p-3 shadow rounded-md gap-2">
                  <!-- <Icon size="xs" icon-name="reviewicon" class-name="text-romanSilver self-start pt-1" /> -->
                  <div class="flex flex-col">
                    <span
                      class="font-black text-xs leading-5 tracking-wide text-blueCrayola uppercase"
                    >
                      {{ cycle.name }}
                    </span>
                    <span
                      class="font-bold text-sm flex leading-6 tracking-wide text-jet uppercase"
                    >
                      {{ $DATEFORMAT(new Date(cycle.start), "MMMM dd, yyyy") }}
                      -
                      {{ $DATEFORMAT(new Date(cycle.end), "MMMM dd, yyyy") }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>

      <ValidationObserver rules="required" v-slot="{ handleSubmit }">
        <div class="px-3" v-if="!isLoading">
          <card class="p-5">
            <div
              class="w-full flex flex-row justify-between"
              style="margin-top: 20px; flex-grow: 1"
            >
              <div class="w-9/12 h-auto mt-2">
                <h1 class="text-base font-bold">Agreement Details</h1>
              </div>

              <div
                class="w-auto flex items-center cursor-pointer gap-1"
                v-if="isApproved"
                @click="viewStatusReports($AuthUser.id, agreementYear)"
              >
                <Icon icon-name="overview-icon" class="light" size="xsm" />
                <p class="text-xs uppercase text-flame font-bold">
                  Status Report Overview
                </p>
              </div>
            </div>

            <div class="border-b-2" />

            <div class="">
              <div class="mt-4">
                <div
                  class="w-full flex flex-row justify-between"
                  style="margin-top: 20px"
                >
                  <div class="flex h-auto mt-2">
                    <h1 class="text-base font-bold">KPAs</h1>
                  </div>

                  <div class="w-auto flex items-center">
                    <div
                      class="w-auto flex cursor-pointer items-center"
                      @click="getEachAgreementComments()"
                    >
                      <Icon
                        icon-name="comment-plus-icon"
                        style="color: white"
                        size="xsm"
                      />
                    </div>

                    <div
                      class="h-4 mx-3 w-px bg-gray-400"
                      v-if="
                        payload.status === 'active' &&
                        employeeSubmitted !== 'pending'
                      "
                    />

                    <div
                      class="w-auto mx-2 flex items-center cursor-pointer gap-1"
                      v-if="
                        payload.status !== 'draft' &&
                        employeeSubmitted !== 'pending'
                      "
                    >
                      <span
                        variant="primary"
                        class="capitalize"
                        :class="badgeClass"
                        style="
                          padding: 4px 8px;
                          font-size: 14px;
                          font-weight: 600;
                          line-height: 1.5;
                          border-radius: 5px;
                          white-space: nowrap;
                        "
                      >
                        {{ badgeLabel }}
                      </span>
                    </div>

                    <div class="h-4 mx-3 w-px bg-gray-400" />

                    <div
                      class="flex items-center justify-between w-full h-full p-2 bg-ghostWhite rounded"
                    >
                      <span class="text-sm font-black whitespace-nowrap">
                        Total Allocated Weight:
                      </span>
                      <div class="flex items-center ml-1 gap-2">
                        <span class="text-sm text-desire">
                          {{ allocatedWeight }}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="border-b-2" style="height: 1rem" />

                <div class="flex flex-col w-full my-4">
                  <!----- List of KPA's ---->

                  <div class="w-full h-auto flex flex-col">
                    <accordion
                      class="mt-6"
                      v-for="(kpaTypes, kpaNumber) in templateKPATypes"
                      :key="'kpa-' + kpaNumber"
                      show-divider
                      :is-open="true"
                    >
                      <template v-slot:title>
                        <ValidationObserver
                          :ref="`kpa-observer-${kpaTypes.id}`"
                          v-slot="{ handleSubmit }"
                        >
                          <div class="flex gap-3">
                            <span class="font-bold">{{ kpaTypes.name }}</span>
                            <span
                              class="flex"
                              :class="
                                isKpaEditable(kpaTypes)
                                  ? 'cursor-pointer opacity-100'
                                  : 'cursor-default opacity-50'
                              "
                              @click="
                                isKpaEditable(kpaTypes)
                                  ? handleSubmit(() => createNewKPA(kpaTypes))
                                  : ' '
                              "
                            >
                              <icon icon-name="icon-plus" size="xs" />
                            </span>
                            <span
                              v-if="payload.status === 'draft' || canSubmit"
                              class="flex cursor-pointer text-blueCrayola"
                              @click="toggleEditMode(kpaTypes)"
                            >
                              <icon
                                :icon-name="
                                  isKpaEditable(kpaTypes)
                                    ? 'icon-save'
                                    : 'icon-edit'
                                "
                                size="xs"
                              />
                            </span>
                          </div>
                        </ValidationObserver>
                      </template>

                      <template v-slot:block>
                        <div class="w-auto flex items-center">
                          <div class="w-auto flex items-center">
                            <Icon
                              icon-name="comment-plus-icon"
                              style="color: white"
                              size="xsm"
                            />
                          </div>

                          <div
                            class="h-4 mx-2 w-px bg-gray-400"
                            v-if="approvedSubmit"
                          />

                          <div
                            class="w-auto flex items-center"
                            v-if="approvedSubmit"
                          >
                            <img
                              src="@/assets/images/hugeicons_assignments.svg"
                              style="width: 24px; height: 24px"
                            />
                          </div>

                          <div class="h-4 mx-2 w-px bg-gray-400" />

                          <div
                            class="p-2 w-auto flex gap-2 items-center rounded-sm mr-2"
                          >
                            <span class="font-black text-xs">
                              {{ getKPAWeight(kpaTypes, kpaTypes.id) }} %
                            </span>
                          </div>
                        </div>
                      </template>

                      <div class="flex flex-col w-full">
                        <!-- Strategic Goal Section -->
                        <div
                          class="w-full mb-6 p-4 bg-alertRed rounded-lg shadow-sm"
                        >
                          <h2 class="text-base font-bold text-gray-800 mb-2">
                            STRATEGIC GOAL
                          </h2>
                          <p class="text-gray-600">
                            {{ kpaTypes.description }}
                          </p>
                        </div>

                        <div class="w-full flex flex-col gap-6">
                          <accordion
                            class="px-5 -mt-1 mb-0 bg-gray-50 shadow-sm rounded-lg"
                            v-for="(item, index) in kpaTypes.kpas"
                            :key="'kpi-' + index"
                            :is-open="true"
                            :iconClass="isKpaEditable(kpaTypes) ? 'mt-4' : ''"
                          >
                            <template v-slot:block>
                              <div
                                class="w-full flex flex-col lg:flex-row items-start justify-between gap-4"
                              >
                                <!-- Left Section (KPI and Objective) -->
                                <div
                                  class="flex items-start w-full lg:w-auto flex-1 gap-2"
                                >
                                  <!-- Delete Button -->
                                  <div
                                    v-if="!item.isLocked"
                                    class="flex items-center"
                                    :class="
                                      !item.isEdit
                                        ? 'h-7 mt-1 cursor-default'
                                        : 'h-8 mt-2  whitespace-nowrap cursor-pointer'
                                    "
                                  >
                                    <span
                                      @click="
                                        !item.isEdit
                                          ? ' '
                                          : deleteKPA(index, kpaTypes)
                                      "
                                      class="text-gray-500 hover:text-red-500 transition-colors"
                                    >
                                      <icon
                                        icon-name="icon-trash"
                                        size="xs"
                                        :class="
                                          !item.isEdit
                                            ? `opacity-50`
                                            : `opacity-100`
                                        "
                                      />
                                    </span>
                                  </div>

                                  <div
                                    :class="
                                      !item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'
                                    "
                                    class="w-auto mx-2 flex items-center"
                                  >
                                    <span
                                      class="w-px bg-gray-300"
                                      :class="!item.isEdit ? 'h-6' : 'h-8'"
                                    ></span>
                                  </div>

                                  <!-- KPI Field -->
                                  <div
                                    v-if="!item.isEdit"
                                    class="flex gap-2 whitespace-nowrap mt-2"
                                  >
                                    <span class="font-bold text-flame text-sm"
                                      >KPI:</span
                                    >
                                    <span
                                      class="font-semibold text-sm"
                                      v-tooltip.bottom-middle="item.name"
                                      >{{ item.name }}</span
                                    >
                                  </div>

                                  <ValidationProvider
                                    v-else
                                    rules="required"
                                    v-slot="{ errors }"
                                    class="min-w-[200px] lg:min-w-[250px]"
                                  >
                                    <div class="flex items-center gap-2 w-full">
                                      <span
                                        class="font-bold text-flame text-sm whitespace-nowrap"
                                        >KPI :</span
                                      >
                                      <c-text
                                        placeholder="--Enter--"
                                        variant="w-full"
                                        :style="'width: 20rem'"
                                        :disabled="item.isLocked"
                                        v-model="item.name"
                                        :error="errors[0]"
                                      />
                                    </div>
                                  </ValidationProvider>

                                  <div
                                    :class="
                                      !item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'
                                    "
                                    class="w-auto mx-2 flex items-center"
                                  >
                                    <span
                                      class="w-px bg-gray-300"
                                      :class="!item.isEdit ? 'h-6' : 'h-8'"
                                    ></span>
                                  </div>

                                  <!-- Objective Field -->
                                  <div
                                    v-if="!item.isEdit"
                                    class="flex gap-2 whitespace-nowrap mt-2"
                                  >
                                    <span class="font-bold text-flame text-sm"
                                      >Objective:</span
                                    >
                                    <span
                                      class="font-semibold text-sm"
                                      v-tooltip.bottom-middle="item.objective"
                                      >{{ item.objective }}</span
                                    >
                                  </div>

                                  <ValidationProvider
                                    v-else
                                    rules="required"
                                    v-slot="{ errors }"
                                    class="flex-1 min-w-[200px] lg:min-w-[250px]"
                                  >
                                    <div class="flex items-center gap-2 w-full">
                                      <span
                                        class="font-bold text-flame uppercase text-sm whitespace-nowrap"
                                        >Objective :</span
                                      >
                                      <c-text
                                        placeholder="--Enter--"
                                        variant="w-full"
                                        class="w-[300px]"
                                        :style="'width: 20rem'"
                                        :disabled="item.isLocked"
                                        v-model="item.objective"
                                        :error="errors[0]"
                                      />
                                    </div>
                                  </ValidationProvider>
                                </div>

                                <div
                                  class="w-full flex items-center justify-end"
                                >
                                  <div
                                    class="flex cursor-pointer items-center"
                                    @click="
                                      getEachAgreementComments(item.kpaId)
                                    "
                                  >
                                    <Icon
                                      icon-name="comment-plus-icon"
                                      style="color: white"
                                      size="xsm"
                                    />
                                  </div>

                                  <div
                                    v-if="approvedSubmit"
                                    :class="
                                      !item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'
                                    "
                                    class="w-auto mx-3 flex items-center"
                                  >
                                    <span
                                      class="w-px bg-gray-300"
                                      :class="!item.isEdit ? 'h-6' : 'h-8'"
                                    ></span>
                                  </div>

                                  <div
                                    v-if="approvedSubmit"
                                    class="flex cursor-pointer items-center"
                                    @click="viewReport(item, kpaTypes)"
                                  >
                                    <img
                                      v-if="
                                        item.report === null ||
                                        item.report === 'null'
                                      "
                                      src="@/assets/images/hugeicons_assignments-gray.svg"
                                      style="width: 24px; height: 24px"
                                    />
                                    <img
                                      v-else
                                      src="@/assets/images/hugeicons_assignments.svg"
                                      style="width: 24px; height: 24px"
                                    />
                                  </div>

                                  <div
                                    :class="
                                      !item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'
                                    "
                                    class="w-auto mx-3 flex items-center"
                                  >
                                    <span
                                      class="w-px bg-gray-300"
                                      :class="!item.isEdit ? 'h-6' : 'h-8'"
                                    ></span>
                                  </div>

                                  <!-- Right Section (Weight) -->
                                  <div
                                    class="w-full lg:w-auto mt-2 mr-3 lg:mt-0"
                                  >
                                    <div
                                      v-if="!item.isEdit"
                                      class="p-2 w-auto flex gap-2 items-center rounded-sm mr-2 mt-1"
                                    >
                                      <span class="font-black text-xs"
                                        >{{ item.weight }}%</span
                                      >
                                    </div>

                                    <ValidationProvider
                                      v-else
                                      rules="required"
                                      v-slot="{ errors }"
                                      class="flex items-center gap-2"
                                    >
                                      <span
                                        class="font-bold text-flame uppercase text-sm whitespace-nowrap"
                                        >Weight :</span
                                      >
                                      <c-text
                                        placeholder="--Enter--"
                                        variant="w-full"
                                        class="w-14"
                                        type="number"
                                        v-model="item.weight"
                                        :disabled="item.isLocked"
                                        :error="errors[0]"
                                        @input="validateWeight(item.weight)"
                                      />
                                    </ValidationProvider>
                                  </div>
                                </div>
                              </div>
                            </template>

                            <div
                              class="w-full h-[1px] mb-6 -mt-3 border border-2"
                            />

                            <div class="w-full flex flex-col gap-1 mb-1">
                              <span class="text-sm font-semibold"
                                >Criteria</span
                              >
                            </div>
                            <div
                              class="w-full grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3"
                            >
                              <div
                                class="label"
                                v-for="(n, rating) in item.kpis"
                                :key="rating"
                              >
                                <ValidationObserver>
                                  <c-text
                                    variant="w-full"
                                    :placeholder="`Criteria`"
                                    v-tooltip.bottom-middle="
                                      item.kpis[rating].criteria
                                    "
                                    :disabled="!item.isEdit"
                                    v-model="item.kpis[rating].criteria"
                                  />
                                </ValidationObserver>
                              </div>
                            </div>
                          </accordion>
                        </div>
                      </div>
                    </accordion>
                  </div>
                </div>
              </div>
            </div>
          </card>
        </div>

        <div class="flex justify-between items-center px-3 mt-5">
          <div
            class="flex gap-4"
            v-if="payload.status === 'draft' || canSubmit"
          >
            <Button
              v-if="showSubmitButton"
              :width="'10rem'"
              class="bg-dynamicBackBtn text-white w-40"
              options
              :option-list="
                employee.reportingTo
                  ? ['Submit', 'Share with manager']
                  : ['Submit']
              "
              @click="
                (value) => {
                  payload.isDraft = false;
                  handleSubmit(checkSelected(value));
                }
              "
            >
              Submit
            </Button>

            <Button
              v-if="!approvedSubmit"
              variant="outline"
              @click="$router.back()"
            >
              Cancel
            </Button>
          </div>

          <Button
            v-if="payload.status === 'draft'"
            variant="outline"
            class="border-flame border text-flame"
            @click="
              () => {
                payload.isDraft = false;
                submit('draft');
              }
            "
          >
            Save as Draft
          </Button>
        </div>
      </ValidationObserver>
    </template>

    <RightSideBar
      v-if="statusModal"
      @close="statusModal = false"
      submit="Save"
      @submit="saveReport"
      button-class="bg-dynamicBackBtn text-white"
      :disabled-button="isSavingReport"
    >
      <template v-slot:title>
        <div class="w-full flex justify-between items-center">
          <span class="text-darkPurple font-bold text-lg leading-5 p-0"
            >Report New</span
          >
          <span
            class="text-blueCrayola font-bold text-sm uppercase cursor-pointer flex"
            @click="
              $router.push({
                name: 'EssEmployeeStatusReports',
                params: { id: $AuthUser.id, year: $route.params.year },
              })
            "
          >
            <Icon
              icon-name="history-icon"
              class="text-blueCrayola"
              size="xms"
            />
            History
          </span>
        </div>
      </template>
      <StatusReport
        :kpasItem="kpasItem"
        :kpaTypes="kpaTypes"
        :edit="true"
        @update-files="updateFiles"
        @update-comment="updateComment"
        @update-support="updateSupport"
        @update-status="updateStatus"
        @kpis-criteriaValues="kpisCriteriaValues"
      />
    </RightSideBar>


    <RightSideBar v-if="commentModal" @submit="sendComment"
      @close="commentModal = false, senderkpaId = '', comment = ''" submit="Save"
      button-class="bg-dynamicBackBtn text-white" :disabled-button="isSavingComment">
      <template v-slot:title>
        <span class="text-darkPurple font-bold text-lg leading-5 p-0"
          >Comments</span
        >
      </template>
      <div class="w-full flex mb-7">
        <div class="flex flex-col w-full" style="min-height: 70vh">
          <CommentsBox
            :comments="allComments"
            :senderId="senderId"
            @markRead="markAsRead($event)"
            :loading="loadComments"
            @deleteComment="deleteComments($event)"
          />
          <div class="-mb-12" style="margin-top: auto">
            <CTextarea
              placeholder="--Message--"
              variant="primary"
              class="mt-2 rounded-lg"
              value=""
              :row="8"
              :height="90"
              :col="30"
              :input="null"
              v-model="comment"
            />
          </div>
        </div>
      </div>
    </RightSideBar>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Datepicker from "vuejs-datepicker";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import Accordion from "@/components/Accordion";
import Badge from "@scelloo/cloudenly-ui/src/components/badge";
import Card from "@/components/Card";
import Button from "@/components/Button";
import RightSideBar from "@/components/RightSideBar";
import StatusReport from "@/components/StatusReport";
import PerformanceDetails from "@/components/PerformanceDetails";
import CommentsBox from "@/components/commentsBox";
import paramsMixin from "@/utilities/paramsMixin";
import timeFormatMixin from "@/utilities/timeFormatMixin";

export default {
  mixins: [paramsMixin, timeFormatMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    Badge,
    Datepicker,
    BackButton,
    Breadcrumb,
    CommentsBox,
    CTextarea,
    Button,
    RightSideBar,
    StatusReport,
    CText,
    Accordion,
    Card,
    PerformanceDetails,
  },
  data() {
    return {
      breadcrumbs: [
        {
          disabled: false,
          text: "Performance Management",
          href: "Performance Management",
          id: "Performance Management",
        },
        {
          disabled: false,
          text: "My Agreements",
          href: "My Agreements",
          id: "My Agreements",
        },
        {
          disabled: false,
          text: "Employee Agreement",
          href: "Employee Agreement",
          id: "Employee Agreement",
        },
      ],
      inputRules: [
        "required",
        {
          name: "data",
          rule: (w) => this.validateEntries(w),
        },
      ],
      weightRules: [
        "required",
        {
          name: "weight",
          rule: (w) => this.validateWeight(w),
        },
      ],
      isLoading: true,
      periodError: false,
      employeeApproved: false,
      canSubmit: false,
      payload: {
        orgId: "",
        templateId: "",
        agreementId: "",
        goalId: "",
        year: "",
        title: "",
        status: "draft",
        created_by: "",
        created_for: "",
        share_for_review: false,
        kpa_types: [],
      },
      employee: {},
      agreementCycles: [],
      employeeSubmitted: "",
      approvedSubmit: false,
      statusModal: false,
      commentModal: false,
      disableBtn: false,
      managerLock: false,
      templateKPATypes: [],
      validateKPA: [],
      Ratings: [],
      kpaWeights: [],
      allocatedWeight: 0,
      managerView: "",
      editingLock: false,
      templateName: "",
      agreementData: {
        period: {},
      },
      loadComments: false,
      allComments: [],
      comment: "",
      senderkpaId: "",
      managerId: "",
      senderId: "",
      kpasItem: {},
      kpaTypes: {},
      files: [],
      reportComment: "",
      requireSupport: "",
      reportStatus: "",
      currentAchievement: "",
      agreementYear: "",
      criteriaValues: "",
      isSavingComment: false,
      isSavingReport: false,
    };
  },
  computed: {
    isApproved() {
      return (
        !!this.employee.reportingTo &&
        this.employeeSubmitted === "submitted" &&
        this.payload.status === "active"
      );
    },
    badgeClass() {
      if (this.employeeSubmitted === "shared") {
        return this.approvedSubmit ? "approvedX" : "blueCrayolaX";
      }
      if (this.employeeSubmitted === "submitted") {
        return this.approvedSubmit ? "approvedX" : "darkPurpleX";
      }
    },
    badgeLabel() {
      if (this.employeeSubmitted === "shared") {
        return this.approvedSubmit ? "Submitted" : "In-Review";
      }
      if (this.employeeSubmitted === "submitted") {
        return this.approvedSubmit ? "Submitted" : "Pending Approval";
      }
      return "";
    },
    showSubmitButton() {
      return this.employeeSubmitted !== "submitted";
    },

    showShareForReviewButton() {
      return (
        this.employee.reportingTo &&
        this.employeeSubmitted !== "submitted" &&
        this.payload.status === "draft"
      );
    },
  },
  methods: {
    isKpaEditable(kpaTypes) {
      return kpaTypes.kpas.some((kpa) => kpa.isEdit);
    },

    toggleEditMode(kpaTypes) {
      if (!this.isKpaEditable(kpaTypes)) {
        kpaTypes.kpas.forEach((kpa) => {
          this.$set(kpa, "isEdit", true);
        });
      } else {
        const allKPAsValid = kpaTypes.kpas.every((kpa) => {
          const hasRequiredFields = kpa.name && kpa.objective && kpa.weight;

          const allKPIsValid =
            !kpa.kpis || kpa.kpis.every((kpi) => kpi.criteria);

          return hasRequiredFields && allKPIsValid;
        });

        if (allKPAsValid) {
          if (this.allocatedWeight > 100) {
            this.$toasted.error(
              `Total weight must be 100%. Current total is ${this.allocatedWeight}%.`,
              { duration: 5000 }
            );
            return;
          }
          kpaTypes.kpas.forEach((kpa) => {
            this.$set(kpa, "isEdit", false);
          });
          this.payload.isDraft = true;
          if (this.payload.status !== "draft") {
            this.checkSelected("Submit");
          } else {
            this.submit("draft");
          }
        } else {
          this.$toasted.error("Please fill all required fields before saving", {
            duration: 5000,
          });
        }
      }
    },

    async viewStatusReports(id, year) {
      this.$router.push({
        name: "EssEmployeeStatusReports",
        params: { id, year },
      });
    },

    async loadInitialData() {
      try {
        this.isLoading = true;

        await Promise.all([
          this.getAgreements(),
          this.getAppraisalCycle(),
          this.getGoals(),
          this.$_getPerfomanceRating().then((response) => {
            this.Ratings = response.data.PerformanceRatings;
          }),
        ]);

        this.isLoading = false; // All data loaded
      } catch (error) {
        this.isLoading = false; // Ensure loading stops even if there's an error
        console.error("Error loading data:", error);
      }
    },

    validateEntries(t) {
      if (t !== "") {
        return true;
      }

      return false;
    },

    async periodSelect(data) {
      this.periodError = false;
      this.payload.year = data.getFullYear();
    },

    getKPAWeight(kpaTypes, kpaTypesId) {
      let sum = 0;

      if (kpaTypes.id === kpaTypesId) {
        kpaTypes.kpas.forEach((kpi) => {
          sum += Number(kpi.weight);
        });
      }

      return sum;
    },

    validateWeight(weight) {
      let sum = 0;
      if (weight < 0) {
        return "Value must be greater than or equal to 0";
      }

      this.templateKPATypes.forEach((kpaType) => {
        kpaType.kpas.map((kpi) => {
          sum += Number(kpi.weight);
          return {};
        });
        return {};
      });

      this.allocatedWeight = sum;

      if (sum !== 100) {
        this.disableBtn = true;
      } else {
        this.disableBtn = false;
      }

      return true;
    },

    checkSelected(value) {
      this.templateKPATypes.forEach((kpaType) => {
        const newKPAs = kpaType.kpas;

        const validation = newKPAs.every(
          (item) => item.name || item.objective || item.weight
        );

        this.validateKPA.push(validation);
      });

      if (this.validateKPA.every((valid) => valid === true)) {
        if (value === "Submit") {
          this.payload.share_for_review = true;
          this.payload.agreement_status = "submitted";
        } else {
          this.payload.share_for_review = true;
          this.payload.agreement_status = "shared";
        }
        this.submit("active");
      } else {
        this.validateEntries("");
      }
    },

    submit(status) {
      this.kpaData = [];
      let hasError = false;
      let totalWeight = 0;
      this.payload.senderId = this.$AuthUser.id;

      this.templateKPATypes.forEach((kpaType) => {
        const newKPAs = kpaType.kpas.map(
          ({ state, isEdit, target_date, ...kpa }) => {
            const hasRequiredKPAFields =
              kpa.name && kpa.objective && kpa.weight;

            // Convert weight to number and add to total
            const weight = Number(kpa.weight) || 0;
            totalWeight += weight;

            const validKpis =
              Array.isArray(kpa.kpis) && kpa.kpis.every((kpi) => kpi.criteria);

            if (!hasRequiredKPAFields || !validKpis) {
              hasError = true;
            }

            return {
              ...kpa,
              kpis: kpa.kpis || [],
            };
          }
        );

        this.kpaData.push({
          kpa_types_id: kpaType.id,
          kpas: newKPAs,
        });
      });

      // Check for errors
      if (hasError && status !== "draft") {
        this.$toasted.error(
          "Please fill in all required KPA and KPI fields (name, objective, weight, and valid criteria).",
          { duration: 5000 }
        );
        return;
      }

      // Check if total weight is 100
      if (totalWeight !== 100 && status !== "draft") {
        this.$toasted.error(
          `Total weight must be 100%. Current total is ${totalWeight}%.`,
          { duration: 5000 }
        );
        return;
      }

      this.payload = {
        ...this.payload,
        kpa_types: this.kpaData,
        orgId: this.$orgId,
        status,
      };

      this.$_createEmployeeAgreement(this.payload)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          if (!this.payload.isDraft) {
            this.$router.push({
              name: "EssAgrreements",
              query: { activeTab: "My Agreements" },
            });
          }
        })
        .catch((error) => {
          this.$toasted.error(error.response.data.message, { duration: 5000 });
        });
    },

    async createNewKPA(kpaType) {
      try {
        // Validate all fields in the current KPA type first
        const isValid = await this.validateKPAType(kpaType);

        if (!isValid) {
          this.$toasted.error(
            "Please fill all required fields before adding a new KPI",
            { duration: 5000 }
          );
          return;
        }

        if (this.allocatedWeight <= 100) {
          kpaType.kpas.push(this.createEmptyKPA());
          this.updateKPAWeights();
        } else {
          this.$toasted.error("Total allocated weight cannot exceed 100%", {
            duration: 5000,
          });
        }
      } catch (error) {
        console.error("Error creating new KPA:", error);
      }
    },

    async validateKPAType(kpaType) {
      // Validate the strategic goal
      // if (!kpaType.strategicGoal?.trim()) {
      //   return false;
      // }

      // Validate all KPAs in this type
      for (const kpa of kpaType.kpas) {
        // Validate basic fields
        if (!kpa.name?.trim() || !kpa.objective?.trim() || !kpa.weight) {
          return false;
        }

        // Validate criteria
        if (kpa.kpis) {
          for (const criteria of kpa.kpis) {
            if (!criteria.criteria?.trim()) {
              return false;
            }
          }
        }
      }

      // If we have a ValidationObserver reference, use it
      if (this.$refs[`kpa-observer-${kpaType.id}`]?.[0]) {
        const isValid = await this.$refs[
          `kpa-observer-${kpaType.id}`
        ][0].validate();
        if (!isValid) {
          return false;
        }
      }

      return true;
    },

    createEmptyKPA() {
      return {
        state: true,
        id: "",
        isEdit: true,
        isLocked: false,
        name: null,
        objective: null,
        kpis: this.getCriteriaData(),
        target_date: null,
        weight: "",
      };
    },

    deleteKPA(index, kpaType) {
      if (kpaType.kpas.length > 1) {
        kpaType.kpas.splice(index, 1);
        let totalWeight = 0;
        this.templateKPATypes.forEach((kpaType) => {
          kpaType.kpas.forEach((kpi) => {
            totalWeight += Number(kpi.weight) || 0;
          });
        });
        this.allocatedWeight = totalWeight;
      } else {
        this.$toasted.error("Cannot delete the only KPI in a KPA Type", {
          duration: 5000,
        });
      }
    },

    updateKPAWeights() {
      this.templateKPATypes.forEach((kpaType) => {
        const sum = this.getKPAWeight(kpaType, kpaType.id);
        const kpaWeight = this.kpaWeights.find((k) => k.id === kpaType.id);
        if (kpaWeight) {
          kpaWeight.weight = sum;
        }
      });
      this.calculateTotalAllocatedWeight();
    },

    calculateTotalAllocatedWeight() {
      this.allocatedWeight = this.templateKPATypes.reduce((total, kpaType) => {
        return total + this.getKPAWeight(kpaType, kpaType.id);
      }, 0);
      this.disableBtn = this.allocatedWeight !== 100 || !this.validateKPAs();
    },

    getGoals() {
      this.$_getOrganizationGoal("?filter=agreement").then((response) => {
        const goalData = response.data.goals;
        goalData.forEach((goals) => {
          if (goals.goal.year === Number("2025")) {
            this.payload.goalId = goals.goal.id;
            this.payload.year = goals.goal.year;
          }
          return {};
        });
      });
    },

    getAgreements() {
      this.senderId = this.$AuthUser.id;
      this.$_getEmployeeAgreement(this.$AuthUser.id).then((response) => {
        const myAgreements = response.data.EmployeePerformanceAgreements;
        this.managerId = response.data.employee.lineManager.id;

        myAgreements.filter((aggreement) => {
          if (aggreement.id === this.$route.params.id) {
            this.payload.templateId = aggreement.templateId;
            this.payload.agreementId = this.$route.params.id;
            this.approvedSubmit = aggreement.approveAndSubmit;
            this.canSubmit = aggreement.canSubmit;
            this.employeeSubmitted = aggreement.agreement_status;
            this.employeeApproved = aggreement.employeeApproved;
            this.payload.status = aggreement.status;
            const agreementKPA = aggreement.employee_agreement_kpa;
            this.payload.created_for = aggreement.created_for;
            this.agreementData.description = aggreement.description;
            this.agreementData.cycle = aggreement.performance_template.title;
            this.agreementYear = aggreement.year;
            this.agreementData.period.start = this.setDates(
              aggreement.performance_template.appraisal_cycle.cycle_start_date
            ).toISOString();
            this.agreementData.period.end = this.setDates(
              aggreement.performance_template.appraisal_cycle.cycle_end_date
            ).toISOString();

            this.viewEmployeeDetails(aggreement.created_for);

            if (aggreement.created_for !== aggreement.created_by) {
              this.managerView = true;
            } else {
              this.managerView = false;
            }
            this.$_getOneTemplate(aggreement.templateId).then((result) => {
              this.templateKPATypes = result.data.PerformanceTemplate.kpa_types;
              this.templateName = result.data.PerformanceTemplate.title;
              this.payload.title =
                result.data.PerformanceTemplate.agreement_title;

              let totalWeight = 0;
              this.templateKPATypes.forEach((kpaType) => {
                kpaType.kpas = [];

                agreementKPA.forEach((employeeKpi) => {
                  if (employeeKpi.kpaTypeId === kpaType.id) {
                    kpaType.kpas.push({
                      state: true,
                      isEdit: false,
                      isLocked: employeeKpi.isLocked,
                      name: employeeKpi.name,
                      objective: employeeKpi.objective,
                      kpaId: employeeKpi.id,
                      kpis: this.getCriteriaData(employeeKpi.kpis_employee),
                      weight: employeeKpi.weight,
                      report: employeeKpi.report,
                    });

                    // Add to total weight
                    totalWeight += employeeKpi.weight || 0;
                  }
                });
              });
              this.allocatedWeight = totalWeight;
              this.getSettings();
            });
          }
          return {};
        });
      });
    },

    viewEmployeeDetails(employeeId) {
      const viewEmployee = [];
      const payload = { id: employeeId };
      const employeeObj = [];

      this.$_getOneEmployeeAll(payload).then((response) => {
        employeeObj.push({
          id: response.data.id,
          fname: response.data.fname,
          lname: response.data.lname,
          photo: response.data.photo,
          employeeId: response.data.employeeId,
          userDesignation: response.data.userDesignation.name,
          reportingTo: response.data.userReportingTo
            ? `${response.data.userReportingTo.fname} ${response.data.userReportingTo.lname}`
            : null,
          office: response.data.employeeOffice?.name,
        });

        viewEmployee.push(
          employeeObj.reduce((r, i) => Object.assign(r, i), {})
        );

        this.employee = Object.assign({}, ...viewEmployee);
      });
    },

    formatEmployeeDate(value) {
      let initialDate = "";

      if (value) {
        initialDate = new Date(value).toISOString().replace(/.\d+Z$/g, "");

        return this.$DATEFORMAT(new Date(initialDate), "MMMM dd, yyyy");
      }

      return "";
    },

    getCriteriaData(kpis) {
      const criteriaData = [];
      const sortedRatings = this.Ratings.sort((a, b) => a.rating - b.rating);

      this.Ratings = sortedRatings;

      if (kpis) {
        this.Ratings.forEach((rating, index) => {
          criteriaData.push({
            id: kpis[index].id,
            performanceRatingId: rating.id,
            criteria: kpis[index].criteria,
          });
        });
      } else {
        this.Ratings.forEach((rating) => {
          criteriaData.push({
            performanceRatingId: rating.id,
            criteria: ``,
          });
        });
      }

      return criteriaData;
    },

    getSettings() {
      this.$_getPerformanceGeneralSetting().then((response) => {
        const settings = response.data.PerformanceSettings;
        this.managerLock = settings[0].managerCanLockAgreement;

        if (this.approvedSubmit) {
          this.editingLock = settings[0].lockSubmittedAgreement;
        }
      });
    },

    getAppraisalCycle() {
      const today = new Date();

      this.$_getAppraisalCycle(this.appraisalQuery()).then((response) => {
        const appraisal = response.data.AppraisalCycle;

        const appraisalCycle = appraisal[0].cycles.sort(
          (a, b) =>
            this.setDates(a.appraisal_starts) -
            this.setDates(b.appraisal_starts)
        );

        appraisalCycle.map((cycle) => {
          const cycleStartDate = new Date(
            this.setDates(cycle.appraisal_starts).toISOString()
          );

          const cycleEndDate = new Date(
            this.setDates(cycle.appraisal_ends).toISOString()
          );

          cycleEndDate.setDate(cycleEndDate.getDate() + 1);
          this.agreementCycles.push({
            name: cycle.name,
            start: this.setDates(cycle.appraisal_starts).toISOString(),
            end: this.setDates(cycle.appraisal_ends).toISOString(),
            active: today >= cycleStartDate && today <= cycleEndDate,
            id: cycle.id,
          });
          return {};
        });
      });
    },
    async getEachAgreementComments(kpiId = "") {
      this.senderkpaId = kpiId;
      const kpi = kpiId ? `?kpiId=${kpiId}` : "";
      this.loadComments = true;
      try {
        const { data } = await this.$_getEachAgreementComments(
          this.payload.agreementId,
          kpi
        );
        this.allComments = data.comments;
        this.commentModal = true;
        this.loadComments = false;
      } catch (error) {
        this.loadComments = false;
        this.senderkpaId = "";
        this.$toasted.error("Error getting comments", { duration: 5000 });
      }
    },
    async sendComment() {
      this.isSavingComment = true;
      const payload = {
        comment: this.comment,
        senderId: this.senderId, // userId of sender
        receiverId: this.managerId, // userId of receiver
        sendTo: "manager",
        performanceAgreementId: this.payload.agreementId,
        ...(this.senderkpaId && { kpiId: this.senderkpaId }),
      };

      try {
        await this.$_sendAgreementComments(payload);
        this.allComments = [];
        this.comment = "";
        this.commentModal = false;
        this.isSavingComment = false;
        this.$toasted.success("Comment successfully sent", { duration: 5000 });
      } catch (error) {
        this.isSavingComment = false;
        this.$toasted.error("Error sending comment", { duration: 5000 });
      }
    },
    updateFiles(files) {
      this.files = files;
    },
    updateSupport(support) {
      this.requireSupport = support;
    },
    updateComment(comment) {
      this.reportComment = comment;
    },
    updateStatus(status) {
      this.reportStatus = status;
    },
    kpisCriteriaValues(criteriaValues) {
      this.criteriaValues = criteriaValues;
    },
    async saveReport() {
      this.isSavingReport = true;
      const payload = {
        orgId: this.$orgId,
        employeeId: this.payload.created_for,
        employeeAgreementId: this.payload.agreementId,
        kpaTypeId: this.kpaTypes.id,
        kpiId: this.kpasItem.kpaId,
        status: this.reportStatus,
        criteria: this.criteriaValues,
        currentAchievement: "",
        requireSupport: this.requireSupport === "yes" ? true : false,
        comment: this.reportComment,
        files: this.files,
        ...(this.kpasItem.report &&
          this.kpasItem.report !== "null" && {
            reportId: this.kpasItem.report.id,
          }),
      };

      try {
        await this.$_saveReport(payload);
        await this.getAgreements();
        await this.$_getPerfomanceRating().then((response) => {
            this.Ratings = response.data.PerformanceRatings;
          })
        this.statusModal = false;
        this.isSavingReport = false;
        this.$toasted.success("Report done successfully", { duration: 5000 });
      } catch (error) {
        this.isSavingReport = false;
        this.$toasted.error(error.response.data.message, { duration: 5000 });
      }
    },
    async viewReport(kpasItem, kpaTypes) {
      this.statusModal = true;
      this.kpasItem = kpasItem;
      this.kpaTypes = kpaTypes;
    },
  },
  async mounted() {
    this.loadInitialData();
  },
};
</script>

<style>
.cloudenly-input {
  padding-right: 8px !important;
  padding-left: 8px !important;
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  height: 38px !important;
}

.date-input {
  width: 100%;
  height: 38px !important;
  outline: none;
  border-radius: 0.375rem;
  padding: 0.5rem;
}

.blueCrayolaX {
  background-color: rgba(241, 90, 41, 0.12) !important;
  color: rgba(241, 90, 41, 1) !important;
}

.darkPurpleX {
  background-color: rgba(233, 147, 35, 0.12) !important;
  color: rgba(233, 147, 35, 1) !important;
}

.approvedX {
  background-color: rgba(33, 118, 255, 0.12) !important;
  color: rgba(33, 118, 255, 1) !important;
}
</style>
